import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Brain, Loader2, PieChart, ShieldAlert, TrendingUp, Globe, DollarSign, Target, BarChart3, FileDown, CheckCircle2, AlertTriangle, ArrowUpRight, ArrowDownRight, Minus, ChevronDown, ChevronRight } from 'lucide-react';
import { HedgePosition } from '@/lib/hedgeAnalysis';
import { useToast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';

interface DiversificationPanelProps {
  positions: HedgePosition[];
}

interface SectorChange {
  sector: string;
  action: string;
  currentPct: string;
  targetPct: string;
  instruments: string;
}

interface CurrencyTarget {
  currency: string;
  currentPct: string;
  targetPct: string;
}

interface ActionStep {
  action: string;
  ticker: string;
  detail: string;
  priority: string;
}

interface ParsedScenario {
  name: string;
  objective: string;
  score: number;
  successChance: number;
  riskLevel: string;
  timeHorizon: string;
  sectorChanges: SectorChange[];
  currencyTargets: CurrencyTarget[];
  risks: string[];
  actionSteps: ActionStep[];
  summary: string;
}

interface LLMScenario {
  modelId: string;
  label: string;
  status: 'pending' | 'fetching' | 'success' | 'error';
  content?: string;
  parsed?: ParsedScenario[];
  error?: string;
}

const SCENARIO_MODELS = [
  { id: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { id: 'openai/gpt-5-mini', label: 'GPT-5 Mini' },
  { id: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
  { id: 'openai/gpt-5', label: 'GPT-5' },
  { id: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro (Finance)' },
];

const TOOL_SCHEMA = {
  type: "function" as const,
  function: {
    name: "diversification_scenarios",
    description: "Return structured diversification scenarios for a portfolio",
    parameters: {
      type: "object",
      properties: {
        scenarios: {
          type: "array",
          items: {
            type: "object",
            properties: {
              name: { type: "string", description: "Scenario title" },
              objective: { type: "string", description: "What this diversification aims to achieve" },
              score: { type: "number", description: "Strategy strength score 1-10" },
              successChance: { type: "number", description: "Probability of success over 12 months (0-100)" },
              riskLevel: { type: "string", enum: ["Low", "Medium", "High"] },
              timeHorizon: { type: "string", description: "When to review/rebalance" },
              summary: { type: "string", description: "2-3 sentence executive summary" },
              sectorChanges: {
                type: "array",
                items: {
                  type: "object",
                  properties: {
                    sector: { type: "string" },
                    action: { type: "string", enum: ["Increase", "Decrease", "Hold", "New"] },
                    currentPct: { type: "string" },
                    targetPct: { type: "string" },
                    instruments: { type: "string", description: "Specific ETFs/stocks to use" },
                  },
                  required: ["sector", "action", "currentPct", "targetPct", "instruments"],
                },
              },
              currencyTargets: {
                type: "array",
                items: {
                  type: "object",
                  properties: {
                    currency: { type: "string" },
                    currentPct: { type: "string" },
                    targetPct: { type: "string" },
                  },
                  required: ["currency", "currentPct", "targetPct"],
                },
              },
              risks: {
                type: "array",
                items: { type: "string" },
                description: "Key risks for this scenario",
              },
              actionSteps: {
                type: "array",
                items: {
                  type: "object",
                  properties: {
                    action: { type: "string", description: "Action verb (Buy, Sell, Hold, Rebalance, etc.)" },
                    ticker: { type: "string", description: "Ticker or instrument name" },
                    detail: { type: "string", description: "Specific instruction with amounts" },
                    priority: { type: "string", enum: ["High", "Medium", "Low"] },
                  },
                  required: ["action", "ticker", "detail", "priority"],
                },
                description: "Exactly 10 actionable steps",
              },
            },
            required: ["name", "objective", "score", "successChance", "riskLevel", "timeHorizon", "summary", "sectorChanges", "currencyTargets", "risks", "actionSteps"],
          },
        },
      },
      required: ["scenarios"],
    },
  },
};

const DiversificationPanel = ({ positions }: DiversificationPanelProps) => {
  const { toast } = useToast();
  const [scenarios, setScenarios] = useState<LLMScenario[]>(
    SCENARIO_MODELS.map(m => ({ modelId: m.id, label: m.label, status: 'pending' }))
  );
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const portfolioBreakdown = useMemo(() => {
    if (!positions.length) return null;
    const totalValue = positions.reduce((s, p) => s + p.totalValue, 0);
    const byCurrency: Record<string, { count: number; value: number; pct: number }> = {};
    positions.forEach(p => {
      const curr = p.localCurrency || 'USD';
      if (!byCurrency[curr]) byCurrency[curr] = { count: 0, value: 0, pct: 0 };
      byCurrency[curr].count += 1;
      byCurrency[curr].value += p.totalValue;
    });
    Object.values(byCurrency).forEach(v => v.pct = (v.value / totalValue) * 100);
    const tickers = positions.map(p => ({
      ticker: p.ticker, name: p.stockName, value: p.totalValue,
      pct: (p.totalValue / totalValue) * 100, gainPct: p.gainPct * 100, currency: p.localCurrency || 'USD',
    })).sort((a, b) => b.value - a.value);
    const top5Pct = tickers.slice(0, 5).reduce((s, t) => s + t.pct, 0);
    return { totalValue, currencies: byCurrency, tickers, top5Pct, positionCount: positions.length };
  }, [positions]);

  const buildPrompt = () => {
    if (!portfolioBreakdown) return '';
    const currencyLines = Object.entries(portfolioBreakdown.currencies)
      .map(([c, v]) => `  ${c}: ${v.count} positions, $${v.value.toLocaleString()} (${v.pct.toFixed(1)}%)`)
      .join('\n');
    const positionLines = portfolioBreakdown.tickers
      .map(t => `  ${t.ticker} (${t.name}): $${t.value.toLocaleString()} (${t.pct.toFixed(1)}%), gain: ${t.gainPct.toFixed(1)}%, currency: ${t.currency}`)
      .join('\n');

    return `Analyse my current stock portfolio and propose 3-4 realistic diversification scenarios.

My Portfolio (${portfolioBreakdown.positionCount} positions, $${portfolioBreakdown.totalValue.toLocaleString()}):

Currency: ${currencyLines}
Top 5 concentration: ${portfolioBreakdown.top5Pct.toFixed(1)}%

Positions:
${positionLines}

For each scenario provide: name, objective, score (1-10), success chance (%), risk level, time horizon, summary, sector changes with current vs target %, currency rebalancing targets, key risks, and exactly 10 actionable steps with ticker, action verb, detail, and priority.

Consider geopolitical tensions, central bank policies, sector rotation, currency hedging, defensive vs growth allocation, commodity/gold exposure, and bond alternatives.

Today: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.`;
  };

  const fetchScenarios = async () => {
    if (!positions.length) {
      toast({ title: 'No portfolio data', description: 'Import your portfolio first.', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setScenarios(SCENARIO_MODELS.map(m => ({ modelId: m.id, label: m.label, status: 'fetching' })));

    const systemPrompt = `You are a senior portfolio strategist. Provide structured diversification scenarios using the tool provided. Be specific with tickers, percentages, and amounts. Each scenario must have exactly 10 action steps.`;
    const userPrompt = buildPrompt();
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` };

    const promises = SCENARIO_MODELS.map(async (m) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 180000);
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            messages: [{ role: 'user', content: userPrompt }],
            model: m.id,
            systemPrompt,
            stream: false,
            tools: [TOOL_SCHEMA],
            tool_choice: { type: "function", function: { name: "diversification_scenarios" } },
          }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          const errText = await response.text();
          return { modelId: m.id, label: m.label, status: 'error' as const, error: `HTTP ${response.status}: ${errText}` };
        }

        const data = await response.json();
        if (data.error) {
          return { modelId: m.id, label: m.label, status: 'error' as const, error: data.error };
        }

        // Try tool call first
        const toolCall = data.choices?.[0]?.message?.tool_calls?.[0];
        if (toolCall?.function?.arguments) {
          try {
            const parsed = JSON.parse(toolCall.function.arguments);
            return { modelId: m.id, label: m.label, status: 'success' as const, parsed: parsed.scenarios || [] };
          } catch {
            // Fall through to content
          }
        }

        // Fallback: raw content
        const content = data.choices?.[0]?.message?.content || 'No response';
        return { modelId: m.id, label: m.label, status: 'success' as const, content };
      } catch (e: any) {
        return { modelId: m.id, label: m.label, status: 'error' as const, error: e.message || 'Request failed' };
      }
    });

    const results = await Promise.all(promises);
    setScenarios(results);
    setLoading(false);

    const successCount = results.filter(r => r.status === 'success').length;
    toast({
      title: successCount > 0 ? 'Diversification Analysis Ready' : 'Analysis Failed',
      description: `${successCount}/${SCENARIO_MODELS.length} models returned scenarios.`,
      variant: successCount > 0 ? 'default' : 'destructive',
    });
  };

  const hasSuccessfulScenarios = scenarios.some(s => s.status === 'success');

  const exportScenariosPDF = () => {
    const successScenarios = scenarios.filter(s => s.status === 'success');
    if (!successScenarios.length) return;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let y = margin;

    const addPageIfNeeded = (space: number) => {
      if (y + space > pageHeight - margin) { doc.addPage(); y = margin; }
    };

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(79, 70, 229);
    doc.text('Portfolio Diversification Scenarios', margin, y);
    y += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${format(new Date(), 'dd MMM yyyy HH:mm')} • ${successScenarios.length} models • ${positions.length} positions`, margin, y);
    y += 4;
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;

    successScenarios.forEach((scenario, mIdx) => {
      addPageIfNeeded(15);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(79, 70, 229);
      doc.text(`Model: ${scenario.label}`, margin, y);
      y += 7;

      const scenarioList = scenario.parsed || [];
      if (scenarioList.length === 0 && scenario.content) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(40, 40, 40);
        const lines = doc.splitTextToSize(scenario.content.replace(/#{1,6}\s*/g, '').replace(/\*\*/g, '').replace(/\*/g, ''), pageWidth - margin * 2);
        for (const line of lines) { addPageIfNeeded(5); doc.text(line, margin, y); y += 4.5; }
        y += 6;
        return;
      }

      scenarioList.forEach((sc, sIdx) => {
        addPageIfNeeded(20);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text(`Scenario ${sIdx + 1}: ${sc.name}`, margin, y);
        y += 5;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        doc.text(`Score: ${sc.score}/10 | Success: ${sc.successChance}% | Risk: ${sc.riskLevel} | Horizon: ${sc.timeHorizon}`, margin, y);
        y += 5;
        const summaryLines = doc.splitTextToSize(sc.objective, pageWidth - margin * 2);
        doc.setTextColor(40, 40, 40);
        for (const l of summaryLines) { addPageIfNeeded(5); doc.text(l, margin, y); y += 4; }
        y += 3;

        // Action steps table
        if (sc.actionSteps?.length) {
          autoTable(doc, {
            startY: y,
            head: [['#', 'Action', 'Ticker', 'Detail', 'Priority']],
            body: sc.actionSteps.map((step, i) => [String(i + 1), step.action, step.ticker, step.detail, step.priority]),
            margin: { left: margin, right: margin },
            styles: { fontSize: 7, cellPadding: 1.5 },
            headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [245, 245, 255] },
          });
          y = (doc as any).lastAutoTable.finalY + 6;
        }

        if (sIdx < scenarioList.length - 1) {
          addPageIfNeeded(6);
          doc.setDrawColor(220, 220, 220);
          doc.setLineDashPattern([1, 1], 0);
          doc.line(margin, y, pageWidth - margin, y);
          doc.setLineDashPattern([], 0);
          y += 5;
        }
      });

      if (mIdx < successScenarios.length - 1) {
        addPageIfNeeded(10);
        doc.setDrawColor(180, 180, 180);
        doc.line(margin, y, pageWidth - margin, y);
        y += 8;
      }
    });

    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(`Diversification Report • Page ${i}/${totalPages}`, margin, pageHeight - 8);
    }
    doc.save(`diversification-scenarios-${format(new Date(), 'yyyy-MM-dd-HHmm')}.pdf`);
    toast({ title: 'PDF Exported', description: 'Diversification scenarios report downloaded.' });
  };

  const getCurrencyColor = (currency: string) => {
    const map: Record<string, string> = {
      USD: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
      EUR: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
      CHF: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30',
      GBP: 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30',
    };
    return map[currency] || 'bg-muted text-muted-foreground border-border';
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 6) return 'text-blue-600 dark:text-blue-400';
    if (score >= 4) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getPriorityBadge = (priority: string) => {
    const p = priority.toLowerCase();
    if (p === 'high') return 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30';
    if (p === 'medium') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30';
    return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
  };

  const getActionIcon = (action: string) => {
    const a = action.toLowerCase();
    if (a === 'increase' || a === 'new' || a === 'buy') return <ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" />;
    if (a === 'decrease' || a === 'sell') return <ArrowDownRight className="h-3.5 w-3.5 text-red-500" />;
    return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
  };

  const renderStructuredScenario = (sc: ParsedScenario, idx: number) => (
    <div key={idx} className="space-y-4">
      {/* Scenario Header */}
      <div className="flex items-start gap-4 bg-gradient-to-r from-primary/5 via-transparent to-transparent rounded-xl p-4 border border-border">
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary font-bold text-lg shrink-0">
          {idx + 1}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-foreground">{sc.name}</h3>
          <p className="text-sm text-muted-foreground mt-1">{sc.objective}</p>
          {sc.summary && <p className="text-xs text-muted-foreground mt-2 italic">{sc.summary}</p>}
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border border-border bg-card p-3 text-center">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Score</p>
          <p className={`text-2xl font-bold ${getScoreColor(sc.score)}`}>{sc.score}<span className="text-sm font-normal text-muted-foreground">/10</span></p>
        </div>
        <div className="rounded-lg border border-border bg-card p-3 text-center">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Success Chance</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{sc.successChance}<span className="text-sm font-normal text-muted-foreground">%</span></p>
        </div>
        <div className="rounded-lg border border-border bg-card p-3 text-center">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Risk Level</p>
          <Badge variant="outline" className={`mt-1 ${
            sc.riskLevel === 'High' ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30' :
            sc.riskLevel === 'Medium' ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' :
            'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
          }`}>{sc.riskLevel}</Badge>
        </div>
        <div className="rounded-lg border border-border bg-card p-3 text-center">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Time Horizon</p>
          <p className="text-sm font-semibold text-foreground mt-1">{sc.timeHorizon}</p>
        </div>
      </div>

      {/* Sector Changes Table */}
      {sc.sectorChanges?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
            <BarChart3 className="h-4 w-4 text-blue-500" />
            Sector Allocation Changes
          </h4>
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider h-9">Sector</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider h-9 text-center w-20">Action</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider h-9 text-right w-20">Current</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider h-9 text-right w-20">Target</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider h-9">Instruments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sc.sectorChanges.map((change, i) => (
                  <TableRow key={i} className="hover:bg-muted/30">
                    <TableCell className="text-xs font-medium py-2">{change.sector}</TableCell>
                    <TableCell className="text-center py-2">
                      <div className="flex items-center justify-center gap-1">
                        {getActionIcon(change.action)}
                        <span className="text-[11px]">{change.action}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-right py-2 font-mono">{change.currentPct}</TableCell>
                    <TableCell className="text-xs text-right py-2 font-mono font-semibold">{change.targetPct}</TableCell>
                    <TableCell className="text-xs py-2 text-muted-foreground">{change.instruments}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Currency Rebalancing */}
      {sc.currencyTargets?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-amber-500" />
            Currency Rebalancing
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {sc.currencyTargets.map((ct, i) => (
              <div key={i} className="rounded-lg border border-border bg-card p-3">
                <p className="text-xs font-bold text-foreground">{ct.currency}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-muted-foreground">{ct.currentPct}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="text-xs font-semibold text-primary">{ct.targetPct}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Risks */}
      {sc.risks?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            Key Risks
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sc.risks.map((risk, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg border border-red-500/15 bg-red-500/5 p-2.5">
                <ShieldAlert className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                <span className="text-xs text-foreground">{risk}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 10-Step Action Plan Table */}
      {sc.actionSteps?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            10-Step Action Plan
          </h4>
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-emerald-500/5">
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider h-9 w-8 text-center">#</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider h-9 w-20">Action</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider h-9 w-24">Ticker</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider h-9">Detail</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider h-9 w-20 text-center">Priority</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sc.actionSteps.map((step, i) => (
                  <TableRow key={i} className="hover:bg-muted/30">
                    <TableCell className="text-center py-2">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold mx-auto">
                        {i + 1}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-semibold">
                        {step.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono font-semibold py-2 text-primary">{step.ticker}</TableCell>
                    <TableCell className="text-xs py-2">{step.detail}</TableCell>
                    <TableCell className="text-center py-2">
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getPriorityBadge(step.priority)}`}>
                        {step.priority}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-10 space-y-6">
      <Card className="border-primary/20 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-500/10 rounded-xl">
                <PieChart className="h-7 w-7 text-indigo-500" />
              </div>
              <div>
                <CardTitle className="text-2xl">Portfolio Diversification</CardTitle>
                <CardDescription>
                  AI-driven diversification scenarios by sector and currency based on your portfolio composition
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasSuccessfulScenarios && (
                <Button variant="outline" size="sm" onClick={exportScenariosPDF} className="gap-1">
                  <FileDown className="h-3 w-3" />
                  Export PDF
                </Button>
              )}
              {positions.length > 0 && (
                <Button onClick={fetchScenarios} disabled={loading} className="gap-2">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
                  {loading ? 'Analysing...' : 'Generate Scenarios'}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Portfolio Breakdown */}
          {portfolioBreakdown && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-primary" />
                    Currency Allocation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {Object.entries(portfolioBreakdown.currencies).sort((a, b) => b[1].pct - a[1].pct).map(([currency, data]) => (
                    <div key={currency} className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] px-1.5 w-12 justify-center ${getCurrencyColor(currency)}`}>
                        {currency}
                      </Badge>
                      <Progress value={data.pct} className="flex-1 h-2" />
                      <span className="text-xs font-mono w-12 text-right">{data.pct.toFixed(1)}%</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Globe className="h-4 w-4 text-primary" />
                    Portfolio Overview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Value</span>
                    <span className="font-semibold">${portfolioBreakdown.totalValue.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Positions</span>
                    <span className="font-semibold">{portfolioBreakdown.positionCount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Top 5 Concentration</span>
                    <Badge variant={portfolioBreakdown.top5Pct > 30 ? 'destructive' : 'secondary'} className="text-xs">
                      {portfolioBreakdown.top5Pct.toFixed(1)}%
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Currencies</span>
                    <span className="font-semibold">{Object.keys(portfolioBreakdown.currencies).length}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    Top Holdings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1.5">
                    {portfolioBreakdown.tickers.slice(0, 5).map(t => (
                      <div key={t.ticker} className="flex items-center justify-between text-xs">
                        <span className="font-mono font-medium">{t.ticker}</span>
                        <span className="text-muted-foreground">{t.pct.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {!positions.length && (
            <div className="text-center py-12 text-muted-foreground">
              <PieChart className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">Import your portfolio to analyse diversification</p>
              <p className="text-sm mt-1">Upload your portfolio Excel file above, then generate AI-driven diversification scenarios</p>
            </div>
          )}

          {/* LLM Scenario Results */}
          {positions.length > 0 && scenarios.every(s => s.status === 'pending') && (
            <div className="text-center py-10 text-muted-foreground border border-dashed border-border rounded-lg">
              <Target className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium">Click "Generate Scenarios" to get AI diversification analysis</p>
              <p className="text-sm mt-1">{SCENARIO_MODELS.length} models will independently analyse your portfolio and propose optimised diversification strategies</p>
            </div>
          )}

          {!scenarios.every(s => s.status === 'pending') && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const allExpanded = scenarios.every(s => expandedCards[s.modelId]);
                    const next: Record<string, boolean> = {};
                    scenarios.forEach(s => { next[s.modelId] = !allExpanded; });
                    setExpandedCards(next);
                  }}
                >
                  {scenarios.every(s => expandedCards[s.modelId])
                    ? <><ChevronDown className="h-4 w-4 mr-1.5" />Collapse All</>
                    : <><ChevronRight className="h-4 w-4 mr-1.5" />Expand All</>
                  }
                </Button>
              </div>
              {scenarios.map((scenario, idx) => (
                <Collapsible
                  key={scenario.modelId}
                  open={expandedCards[scenario.modelId] ?? (idx === 0 && scenario.status === 'success')}
                  onOpenChange={(open) => setExpandedCards(prev => ({ ...prev, [scenario.modelId]: open }))}
                >
                <Card className={`border-2 overflow-hidden ${
                  scenario.status === 'fetching' ? 'border-primary/40 animate-pulse' :
                  scenario.status === 'success' ? 'border-emerald-500/30' :
                  scenario.status === 'error' ? 'border-destructive/30' : 'border-border'
                }`}>
                  {/* Model header bar — acts as collapse trigger */}
                  <CollapsibleTrigger asChild>
                    <div className={`px-5 py-3 flex items-center justify-between cursor-pointer select-none transition-colors hover:bg-muted/40 ${
                      scenario.status === 'success'
                        ? 'bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent border-b border-emerald-500/20'
                        : scenario.status === 'error'
                        ? 'bg-destructive/5 border-b border-destructive/20'
                        : 'bg-muted/30 border-b border-border'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold ${
                          scenario.status === 'success'
                            ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {idx + 1}
                        </div>
                        <div>
                          <CardTitle className="text-base font-semibold">{scenario.label}</CardTitle>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {scenario.status === 'success'
                              ? `${scenario.parsed?.length || 0} scenarios generated`
                              : scenario.status === 'fetching' ? 'Generating analysis…'
                              : scenario.status === 'error' ? 'Analysis failed' : 'Pending'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-xs ${
                          scenario.status === 'fetching' ? 'bg-primary/10 text-primary border-primary/30' :
                          scenario.status === 'success' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' :
                          scenario.status === 'error' ? 'bg-destructive/10 text-destructive border-destructive/30' : ''
                        }`}>
                          {scenario.status === 'fetching' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                          {scenario.status}
                        </Badge>
                        {(expandedCards[scenario.modelId] ?? (idx === 0 && scenario.status === 'success'))
                          ? <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform" />
                          : <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform" />
                        }
                      </div>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardContent className="p-5">
                      {scenario.status === 'fetching' && (
                        <div className="space-y-3">
                          <div className="h-5 bg-muted rounded animate-pulse w-2/3" />
                          <div className="h-4 bg-muted rounded animate-pulse w-full" />
                          <div className="h-4 bg-muted rounded animate-pulse w-5/6" />
                          <div className="h-4 bg-muted rounded animate-pulse w-4/5" />
                          <div className="h-20 bg-muted rounded animate-pulse w-full mt-4" />
                        </div>
                      )}
                      {scenario.status === 'success' && scenario.parsed && scenario.parsed.length > 0 && (
                        <div className="space-y-8">
                          {scenario.parsed.map((sc, i) => renderStructuredScenario(sc, i))}
                        </div>
                      )}
                      {scenario.status === 'success' && (!scenario.parsed || scenario.parsed.length === 0) && scenario.content && (
                        <div className="diversification-scenario prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed">
                          <ReactMarkdown>{scenario.content}</ReactMarkdown>
                        </div>
                      )}
                      {scenario.status === 'error' && (
                        <p className="text-sm text-destructive">{scenario.error}</p>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DiversificationPanel;
